// ABOUTME: Comprehensive family management page for editing family information and managing members  
// ABOUTME: Provides sections for family info, member management, and child access control with modal-based editing

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Avatar,
  Tooltip,
  InputAdornment,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Group as GroupIcon,
  ChildCare as ChildIcon,
  Refresh as RefreshIcon,
  AdminPanelSettings as AdminIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
  FlightTakeoff as FlightTakeoffIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import naluV1Theme from '../theme/theme.nalu-v1';
import { getChildColor, getChildBorderColor } from '../utils/childColorUtils';
import { useAuth } from '../contexts/AuthContext';
import { useSignalR } from '../contexts/SignalRContext';
import { familyAPI, roleAPI, subscriptionAPI, calendarAPI, awayScheduleAPI, behaviorAPI } from '../services/api';
import { CalendarConnectionDto, FamilyAwaySchedule } from '../types/api';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { useCheckoutOverlay } from '../hooks/useCheckoutOverlay';
import { useFamilyTerminology } from '../hooks/useFamilyTerminology';
import SharedAppBar from './SharedAppBar';
import AwayScheduleCalendar from './AwayScheduleCalendar';
import AvatarUploadModal from './AvatarUploadModal';
import RoleEditModal from './RoleEditModal';
import EditFamilyInfoModal from './MyFamilyPage/modals/EditFamilyInfoModal';
import AddMemberModal from './MyFamilyPage/modals/AddMemberModal';
import RemoveMemberDialog from './MyFamilyPage/modals/RemoveMemberDialog';
import RemoveChildDialog from './MyFamilyPage/modals/RemoveChildDialog';
import RegenerateTokenDialog from './MyFamilyPage/modals/RegenerateTokenDialog';
import CancelInvitationDialog from './MyFamilyPage/modals/CancelInvitationDialog';
import CalendarSourcesSection from './MyFamilyPage/CalendarSourcesSection';
import { getAvatarUrl } from '../utils/avatarUtils';
import {
  validateEmail,
  validateFirstName,
  validateLastName,
  validatePassword,
  validateConfirmPassword
} from '../utils/familyValidation';
import {
  getRemovalRestrictionReason
} from '../utils/familyPermissions';
import type {
  FamilyInfo,
  FamilyMember,
  ChildInfo,
  ChildAccessToken,
  SubscriptionStatus,
  EditFamilyInfoForm,
  AddMemberForm,
  PendingInvitation
} from './MyFamilyPage/types';
import { RoleSuggestion } from '../types/api';

// Extracts a user-facing message from a caught error, preferring the API's response
// payload (checked under dataField) and falling back to the generic Error message.
function getErrorMessage(err: unknown, dataField: 'message' | 'error' = 'message'): string | undefined {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as Record<string, string> | undefined;
    return data?.[dataField] || err.message;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return undefined;
}

const MyFamilyPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { on, connectionState } = useSignalR();
  // Track previous connectionState to detect Disconnected→Connected transitions for re-fetch
  const prevConnectionStateRef = useRef<string>(connectionState);
  const { Term, basePath } = useFamilyTerminology();
  const theme = useTheme();
  // ThemeProvider's resolved theme and this module's naluV1Theme import different
  // object instances under Vite's dev-server HMR, so compare a stable value instead of `===`.
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  // Family Info hero card's field labels/values (issue #1900) — mockup renders labels as
  // small uppercase tracked text and values as bold sans-serif, distinct from the Newsreader
  // serif headings used elsewhere on the page.
  const naluHeroLabelSx = isNaluV1Theme
    ? { fontSize: '11px', fontWeight: 800, letterSpacing: '1.5px', textTransform: 'uppercase' as const, opacity: 0.75 }
    : { opacity: 0.8 };
  const naluHeroValueSx = isNaluV1Theme
    ? { fontFamily: theme.typography.fontFamily, fontWeight: 700, fontSize: '16px' }
    : undefined;

  // Data state
  const [familyInfo, setFamilyInfo] = useState<FamilyInfo | null>(null);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [children, setChildren] = useState<ChildInfo[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
  const [childAccessTokens, setChildAccessTokens] = useState<ChildAccessToken[]>([]);
  const [, setCalendarConnections] = useState<CalendarConnectionDto[]>([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Modal state
  const [editFamilyInfoModalOpen, setEditFamilyInfoModalOpen] = useState(false);
  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false);
  const [removeMemberDialogOpen, setRemoveMemberDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<FamilyMember | null>(null);
  const [regenerateTokenDialogOpen, setRegenerateTokenDialogOpen] = useState(false);
  const [selectedToken, setSelectedToken] = useState<ChildAccessToken | null>(null);
  const [removeChildDialogOpen, setRemoveChildDialogOpen] = useState(false);
  const [selectedChildForRemoval, setSelectedChildForRemoval] = useState<ChildInfo | null>(null);
  const [cancelInvitationDialogOpen, setCancelInvitationDialogOpen] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState<PendingInvitation | null>(null);
  const [cancelSubscriptionDialogOpen, setCancelSubscriptionDialogOpen] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);

  // Role editing state
  const [roleEditModalOpen, setRoleEditModalOpen] = useState(false);
  const [editingMemberRole, setEditingMemberRole] = useState<{userId: number, currentRole: string} | null>(null);
  const [roleSuggestions, setRoleSuggestions] = useState<RoleSuggestion[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  
  // Avatar editing state
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [avatarEditingEntity, setAvatarEditingEntity] = useState<{
    type: 'user' | 'child';
    id: number;
    currentAvatarUrl?: string;
  } | null>(null);
  
  // Form state
  const [addMemberForm, setAddMemberForm] = useState<AddMemberForm>({
    firstName: '',
    lastName: '',
    memberEmail: '',
    memberRole: 'Mom',
    isPrimaryCaregiver: false,
    invitationMethod: 'email',
    mantra: '',
    familyLoveQuestion: '',
    familyNeedQuestion: '',
    customMessage: '',
    password: '',
    confirmPassword: ''
  });
  // Validation state
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [memberEmailError, setMemberEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  
  // Success/error messages
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  
  // Loading states for individual sections
  const [, setSavingFamilyInfo] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [regeneratingToken, setRegeneratingToken] = useState(false);
  
  // Password visibility state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Copy button state for child access URLs
  const [copyButtonStates, setCopyButtonStates] = useState<Record<string, boolean>>({});

  // Floating snackbar message state (separate from inline alerts)
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [showFloatingSnackbar, setShowFloatingSnackbar] = useState(false);

  // Away schedule state (issue #1706)
  const [awaySchedule, setAwaySchedule] = useState<FamilyAwaySchedule>({ recurringDaysOfWeek: [], overrides: [] });
  const [awayScheduleLoading, setAwayScheduleLoading] = useState(false);
  const [awayScheduleError, setAwayScheduleError] = useState<string | null>(null);
  // Past-day away status from DailyProgress records, keyed by date string
  const [pastDayStatus, setPastDayStatus] = useState<Map<string, boolean>>(new Map());

  // Issue #1030 - Checkout success snackbar
  const [checkoutSuccessMessage, setCheckoutSuccessMessage] = useState<string | null>(null);
  const [showCheckoutSuccess, setShowCheckoutSuccess] = useState(false);

  // Check if current user is Primary Caregiver or Admin (admins can manage any family)
  const isPrimaryCaregiver = user?.isAdmin || user?.role === 'Owner' || familyMembers.find(m => m.userId === user?.userId)?.isPrimaryCaregiver || false;

  // Issue #1030 - Initialize checkout overlay hook
  const { openCheckout, isLoading: checkoutLoading } = useCheckoutOverlay({
    familyId: familyInfo?.familyId || 0,
    email: user?.email || '',
    onSuccess: () => {
      // Show success message
      setCheckoutSuccessMessage('Payment successful! Your subscription is being activated...');
      setShowCheckoutSuccess(true);
      // Auto-hide after 10 seconds
      setTimeout(() => {
        setCheckoutSuccessMessage(null);
        setShowCheckoutSuccess(false);
      }, 10000);
      // Reload family data to get updated subscription status
      loadFamilyData();
    },
    onError: (error) => {
      console.error('Checkout error:', error);
      setError(error.message);
    }
  });

  // Issue #1031 - Real-time subscription polling
  const {
    error: pollingError // eslint-disable-line @typescript-eslint/no-unused-vars
  } = useSubscriptionStatus({
    enabled: true, // Always poll on MyFamilyPage
    aggressivePolling: checkoutLoading || subscriptionStatus?.status === 'past_due',
  });

  // Load role suggestions function (memoized to prevent infinite re-renders)
  const loadRoleSuggestions = React.useCallback(async () => {
    try {
      setLoadingRoles(true);
      const suggestions = await roleAPI.getRoleSuggestions();
      setRoleSuggestions(suggestions);
    } catch (err) {
      console.error('Error loading role suggestions:', err);
      // Set default roles if API fails
      setRoleSuggestions([
        { id: 1, roleName: 'Parent', isActive: true, isPriority: true, displayOrder: 1, createdAt: new Date().toISOString() },
        { id: 2, roleName: 'Guardian', isActive: true, isPriority: true, displayOrder: 2, createdAt: new Date().toISOString() },
        { id: 3, roleName: 'Caregiver', isActive: true, isPriority: true, displayOrder: 3, createdAt: new Date().toISOString() }
      ]);
    } finally {
      setLoadingRoles(false);
    }
  }, []); // Empty deps - function doesn't depend on any props/state

  // Load initial data
  useEffect(() => {
    loadFamilyData();
  }, []);

  // Subscribe to real-time family and child events and reload data
  useEffect(() => {
    const unsubFamily = on('FamilyUpdated', () => loadFamilyData());
    const unsubChild = on('ChildUpdated', () => loadFamilyData());
    return () => {
      unsubFamily();
      unsubChild();
    };
  }, [on]);

  // Re-fetch family data when SignalR reconnects (Disconnected→Connected transition).
  useEffect(() => {
    if (prevConnectionStateRef.current !== 'Connected' && connectionState === 'Connected') {
      loadFamilyData();
    }
    prevConnectionStateRef.current = connectionState;
  }, [connectionState]);

  const loadFamilyData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Load family info and members
      const families = await familyAPI.getFamilies();
      if (families.length > 0) {
        const familySummary = families[0]; // Get first family summary

        // Get full family details including timezone using familyAPI (includes impersonation header)
        const familyDetails = await familyAPI.getFamilyDetails(familySummary.familyId);

        setFamilyInfo({
          familyId: familyDetails.familyId,
          name: familyDetails.name,
          timezone: familyDetails.timezone || '', // Use actual family timezone from API
          description: familyDetails.description || '',
          effectiveDailyLoggingGoalPerChild: familyDetails.effectiveDailyLoggingGoalPerChild,
          effectiveThemeName: familyDetails.effectiveThemeName,
          effectiveLabelLanguageMode: familyDetails.effectiveLabelLanguageMode
        });

        // Load family members
        const familyMembersData = await familyAPI.getFamilyMembers(familySummary.familyId);
        const members: FamilyMember[] = familyMembersData.map(member => ({
          userId: member.userId,
          email: member.email,
          firstName: member.firstName,
          lastName: member.lastName,
          profileImageUrl: member.profileImageUrl,
          role: member.customRole || (member.role === 1 ? 'Owner' : 'Adult'), // Use custom role or fallback to enum
          isPrimaryCaregiver: member.role === 1, // FamilyRole.Owner = 1
          joinedAt: member.joinedAt,
          isBillingOwner: member.isBillingOwner // Issue #1025
        }));
        setFamilyMembers(members);
        
        // Load children
        const familyChildren = await familyAPI.getChildren(familySummary.familyId);
        const childrenData: ChildInfo[] = familyChildren.map(child => ({
          childId: child.childId || child.id || 0,
          name: child.name,
          aliases: child.aliases,
          profileImageUrl: child.profileImageUrl,
          age: child.age || new Date().getFullYear() - child.birthYear,
          birthMonth: child.birthMonth,
          gender: child.gender,
          mantra: child.mantra,
          familyLoveQuestion: child.familyLoveQuestion,
          familyNeedQuestion: child.familyNeedQuestion
        }));
        setChildren(childrenData);
        
        // Load pending invitations
        try {
          const invitations = await familyAPI.getFamilyInvitations(familySummary.familyId);
          setPendingInvitations(invitations);
        } catch (err) {
          console.warn('Error loading pending invitations:', err);
          setPendingInvitations([]);
        }
        
        // Load child access tokens from API
        try {
          const tokens = await familyAPI.getChildAccessTokens(familySummary.familyId);
          setChildAccessTokens(tokens);
        } catch (tokenError) {
          console.error('Failed to load child access tokens:', tokenError);
          // Fallback to empty array on API failure
          setChildAccessTokens([]);
        }

        // Load calendar connections
        try {
          const connections = await calendarAPI.getConnections();
          setCalendarConnections(connections);
        } catch {
          setCalendarConnections([]);
        }

        // Load away schedule
        try {
          const schedule = await awayScheduleAPI.getSchedule(familySummary.familyId);
          setAwaySchedule(schedule);
        } catch {
          // Away schedule is optional — leave defaults if not available
        }

        // Load subscription status (Issue #1028)
        try {
          setSubscriptionLoading(true);
          setSubscriptionError(null);
          const subscriptionData = await subscriptionAPI.getSubscriptionStatus();
          setSubscriptionStatus(subscriptionData);
        } catch (err: unknown) {
          if (axios.isAxiosError(err) && err.response?.status === 404) {
            // No subscription found
            setSubscriptionStatus(null);
            setSubscriptionError('not_found');
          } else {
            console.error('Error loading subscription status:', err);
            setSubscriptionError(getErrorMessage(err) || 'Failed to load subscription status');
            setSubscriptionStatus(null);
          }
        } finally {
          setSubscriptionLoading(false);
        }
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err) || 'Failed to load family data');
    } finally {
      setIsLoading(false);
    }
  };

  // Reload calendar connections when sources are added/removed/refreshed
  const handleCalendarConnectionsChanged = async () => {
    try {
      const connections = await calendarAPI.getConnections();
      setCalendarConnections(connections);
    } catch {
      // CalendarSourcesSection handles its own errors; just refresh parent state
    }
  };

  // Family info editing
  const handleEditFamilyInfo = () => {
    if (familyInfo) {
      setEditFamilyInfoModalOpen(true);
    }
  };

  // Note: formData comes from EditFamilyInfoModal's own internal form state (passed via its
  // onSave callback) — it must be used directly here, not read from a separate parent-level
  // form state, since the modal is the one the user actually types into.
  const handleSaveFamilyInfo = async (formData: EditFamilyInfoForm) => {
    try {
      setSavingFamilyInfo(true);

      // Update family info via API
      await familyAPI.updateFamilyInfo(familyInfo!.familyId, formData);

      // Also update family settings (daily logging goal, theme, bilingual label mode) via a
      // separate endpoint. All fields must be sent together — the endpoint overwrites all of
      // them from the request body, so sending only the changed field would clear the others to null.
      if ((formData.dailyLoggingGoalPerChild !== null &&
           formData.dailyLoggingGoalPerChild !== familyInfo?.effectiveDailyLoggingGoalPerChild) ||
          formData.themeName !== familyInfo?.effectiveThemeName ||
          formData.labelLanguageMode !== familyInfo?.effectiveLabelLanguageMode) {
        await familyAPI.updateFamilySettings({
          dailyLoggingGoalPerChild: formData.dailyLoggingGoalPerChild,
          themeName: formData.themeName,
          labelLanguageMode: formData.labelLanguageMode
        });
      }

      // Update local state
      if (familyInfo) {
        setFamilyInfo({
          ...familyInfo,
          name: formData.familyName,
          timezone: formData.timezone,
          effectiveDailyLoggingGoalPerChild: formData.dailyLoggingGoalPerChild || familyInfo.effectiveDailyLoggingGoalPerChild,
          effectiveThemeName: formData.themeName,
          effectiveLabelLanguageMode: formData.labelLanguageMode
        });
      }

      setEditFamilyInfoModalOpen(false);
    } catch (err: unknown) {
      setError(getErrorMessage(err) || 'Failed to update family information');
    } finally {
      setSavingFamilyInfo(false);
    }
  };

  // Resend invitation functionality
  const handleResendInvitation = async (invitationId: number) => {
    try {
      await familyAPI.resendInvitation(invitationId);

      // Show success and refresh pending invitations
      setError(null); // Clear any previous errors
      setSuccessMessage('Invitation resent successfully!');
      await loadFamilyData();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err) || 'Unknown error';
      setError(`Failed to resend invitation: ${errorMessage}`);
    }
  };

  // Cancel invitation functionality
  const handleCancelInvitation = async (invitation: PendingInvitation) => {
    setSelectedInvitation(invitation);
    setCancelInvitationDialogOpen(true);
  };

  const handleConfirmCancelInvitation = async () => {
    if (!selectedInvitation) return;

    try {
      await familyAPI.cancelInvitation(selectedInvitation.id);

      // Show success and refresh pending invitations
      setError(null); // Clear any previous errors
      setSuccessMessage('Invitation cancelled successfully!');
      await loadFamilyData();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err) || 'Unknown error';
      setError(`Failed to cancel invitation: ${errorMessage}`);
    } finally {
      setCancelInvitationDialogOpen(false);
      setSelectedInvitation(null);
    }
  };

  const handleCancelInvitationDialog = () => {
    setCancelInvitationDialogOpen(false);
    setSelectedInvitation(null);
  };

  // Member management
  const handleAddMember = () => {
    setAddMemberForm({
      firstName: '',
      lastName: '',
      memberEmail: '',
      memberRole: 'Parent',
      isPrimaryCaregiver: false,
      invitationMethod: 'email',
      mantra: '',
      familyLoveQuestion: '',
      familyNeedQuestion: '',
      customMessage: '',
      password: '',
      confirmPassword: ''
    });
    setFirstNameError('');
    setLastNameError('');
    setMemberEmailError('');
    setPasswordError('');
    setConfirmPasswordError('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setAddMemberModalOpen(true);
    loadRoleSuggestions();
  };

  const handleAddChild = () => {
    navigate('/family/child/new');
  };

  const handleSaveNewMember = async () => {
    const firstNameError = validateFirstName(addMemberForm.firstName);
    const lastNameError = validateLastName(addMemberForm.lastName);
    const emailError = validateEmail(addMemberForm.memberEmail);
    
    // Password validation only for direct addition
    let passwordError = '';
    let confirmPasswordError = '';
    if (addMemberForm.invitationMethod === 'direct') {
      passwordError = validatePassword(addMemberForm.password || '');
      confirmPasswordError = validateConfirmPassword(addMemberForm.password || '', addMemberForm.confirmPassword || '');
    }
    
    setFirstNameError(firstNameError);
    setLastNameError(lastNameError);
    setMemberEmailError(emailError);
    setPasswordError(passwordError);
    setConfirmPasswordError(confirmPasswordError);
    
    if (firstNameError || lastNameError || emailError || passwordError || confirmPasswordError) {
      return;
    }
    
    try {
      setAddingMember(true);
      
      // Add member via API
      const apiResponse = await familyAPI.addFamilyMember(familyInfo!.familyId, {
        ...addMemberForm,
        password: addMemberForm.invitationMethod === 'direct' ? addMemberForm.password : undefined
      });

      if (addMemberForm.invitationMethod === 'email') {
        // Email invitation sent - apiResponse is InvitationDto
        setError(null);
        setSuccessMessage('Invitation sent successfully!');

        // Refresh pending invitations
        await loadFamilyData();

        setAddMemberModalOpen(false);

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        // Direct addition - apiResponse is FamilyMemberDto
        const memberDto = apiResponse as import('../types/api').FamilyMemberDto;
        const newMember: FamilyMember = {
          userId: memberDto.userId, // Use real userId from API response
          email: memberDto.email,
          firstName: memberDto.firstName,
          lastName: memberDto.lastName,
          profileImageUrl: memberDto.profileImageUrl,
          role: memberDto.customRole || (memberDto.role === 1 ? 'Owner' : 'Adult'),
          isPrimaryCaregiver: memberDto.role === 1, // FamilyRole.Owner = 1
          joinedAt: memberDto.joinedAt
        };

        setFamilyMembers([...familyMembers, newMember]);
        setAddMemberModalOpen(false);
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err) || 'Failed to add family member');
    } finally {
      setAddingMember(false);
    }
  };


  const handleRemoveMember = (member: FamilyMember) => {
    setSelectedMember(member);
    setRemoveMemberDialogOpen(true);
  };

  const confirmRemoveMember = async () => {
    if (!selectedMember) return;

    try {
      // Remove member via API
      await familyAPI.removeFamilyMember(familyInfo!.familyId, selectedMember.userId);

      // Update local state
      setFamilyMembers(familyMembers.filter(m => m.userId !== selectedMember.userId));
      setRemoveMemberDialogOpen(false);
      setSelectedMember(null);
      setSuccessMessage('Family member removed successfully');
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err) || 'Failed to remove family member';
      setError(errorMessage);
      // Keep the dialog open so user can see the error
    }
  };

  // Child management
  const handleEditChild = (child: ChildInfo) => {
    navigate(`/family/child/${child.childId}`);
  };

  // Token management
  const handleRegenerateToken = (token: ChildAccessToken) => {
    setSelectedToken(token);
    setRegenerateTokenDialogOpen(true);
  };

  const confirmRegenerateToken = async () => {
    if (!selectedToken) return;
    
    try {
      setRegeneratingToken(true);
      
      // Regenerate token via API
      const result = await familyAPI.regenerateChildAccessToken(selectedToken.childId, selectedToken.dashboardType ?? '');
      
      // Update local state with new URL from API
      setChildAccessTokens(tokens => 
        tokens.map(token => 
          token.childId === selectedToken.childId && token.dashboardType === selectedToken.dashboardType
            ? { ...token, accessUrl: result.accessUrl, createdAt: new Date().toISOString() }
            : token
        )
      );
      
      setRegenerateTokenDialogOpen(false);
      setSelectedToken(null);
    } catch (err: unknown) {
      setError(getErrorMessage(err) || 'Failed to regenerate access token');
    } finally {
      setRegeneratingToken(false);
    }
  };

  // Child removal handlers
  const handleRemoveChild = (child: ChildInfo) => {
    setSelectedChildForRemoval(child);
    setRemoveChildDialogOpen(true);
  };

  const confirmRemoveChild = async () => {
    if (!selectedChildForRemoval) return;
    
    try {
      // Remove child via API
      await familyAPI.removeChild(selectedChildForRemoval.childId);
      
      // Refresh family data to reflect the removal
      await loadFamilyData();
      
      setRemoveChildDialogOpen(false);
      setSelectedChildForRemoval(null);
    } catch (err: unknown) {
      setError(getErrorMessage(err) || 'Failed to remove child');
    }
  };

  /**
   * Handles avatar save operation for family members.
   * Child avatar editing is handled within ChildProfilePage.
   */
  const handleAvatarSave = async (_avatarUrl: string | null) => {
    // Note: User avatar updates go through the existing user profile system
    setAvatarModalOpen(false);
    setAvatarEditingEntity(null);
  };

  // Real-time validation handlers
  const handleFirstNameChange = (value: string) => {
    setAddMemberForm({ ...addMemberForm, firstName: value });
    setFirstNameError(validateFirstName(value));
  };

  const handleLastNameChange = (value: string) => {
    setAddMemberForm({ ...addMemberForm, lastName: value });
    setLastNameError(validateLastName(value));
  };

  const handleMemberEmailChange = (value: string) => {
    setAddMemberForm({ ...addMemberForm, memberEmail: value });
    setMemberEmailError(validateEmail(value));
  };

  const handlePasswordChange = (value: string) => {
    setAddMemberForm({ ...addMemberForm, password: value });
    setPasswordError(validatePassword(value));
    // Re-validate confirm password if it's been entered
    if (addMemberForm.confirmPassword) {
      setConfirmPasswordError(validateConfirmPassword(value, addMemberForm.confirmPassword));
    }
  };

  const handleConfirmPasswordChange = (value: string) => {
    setAddMemberForm({ ...addMemberForm, confirmPassword: value });
    setConfirmPasswordError(validateConfirmPassword(addMemberForm.password || '', value));
  };

  // Role editing handlers
  const handleEditMemberRole = (member: FamilyMember) => {
    setEditingMemberRole({ userId: member.userId, currentRole: member.role });
    setRoleEditModalOpen(true);
  };

  const handleSaveMemberRole = async (newRole: string) => {
    if (!editingMemberRole || !familyInfo) return;
    
    try {
      // Update role via API
      await roleAPI.updateUserRole(editingMemberRole.userId, familyInfo.familyId, newRole);
      
      // Update local state
      setFamilyMembers(familyMembers.map(member => 
        member.userId === editingMemberRole.userId 
          ? { ...member, role: newRole }
          : member
      ));
      
      setRoleEditModalOpen(false);
      setEditingMemberRole(null);
    } catch (err: unknown) {
      setError(getErrorMessage(err) || 'Failed to update member role');
    }
  };

  // Away schedule handlers (issue #1706)
  const handleToggleAwayDay = async (dayOfWeek: number) => {
    if (!familyInfo) return;
    const current = awaySchedule.recurringDaysOfWeek;
    const updated = current.includes(dayOfWeek)
      ? current.filter(d => d !== dayOfWeek)
      : [...current, dayOfWeek].sort((a, b) => a - b);
    try {
      setAwayScheduleLoading(true);
      setAwayScheduleError(null);
      const result = await awayScheduleAPI.updateRecurringDays(familyInfo.familyId, updated);
      setAwaySchedule(result);
    } catch {
      setAwayScheduleError('Failed to update away days. Please try again.');
    } finally {
      setAwayScheduleLoading(false);
    }
  };


  // Load past-day away status when calendar month changes (issue #1706)
  const handleAwayCalendarMonthChange = useCallback(async (monthStart: string, monthEnd: string) => {
    if (!children.length) return;
    try {
      // Use first child's progress — all children share the same family away schedule
      const firstChildId = children[0].childId;
      const progress = await behaviorAPI.getChildProgressHistorySummary(
        firstChildId,
        new Date(monthStart),
        new Date(monthEnd)
      );
      const statusMap = new Map<string, boolean>();
      progress.forEach(dp => {
        if (dp.date) {
          statusMap.set(dp.date, dp.isAway ?? false);
        }
      });
      setPastDayStatus(statusMap);
    } catch {
      // Non-critical — calendar still works with schedule-based logic for past days
    }
  }, [children]);

  // Calendar-based toggle for away day overrides (issue #1706)
  const handleCalendarToggleDate = async (date: string, isAway: boolean | null) => {
    if (!familyInfo) return;
    try {
      setAwayScheduleLoading(true);
      setAwayScheduleError(null);
      const result = await awayScheduleAPI.setDateOverride(familyInfo.familyId, date, isAway);
      setAwaySchedule(result);
    } catch {
      setAwayScheduleError('Failed to update date. Please try again.');
    } finally {
      setAwayScheduleLoading(false);
    }
  };

  // Copy URL functionality
  const handleCopyUrl = async (token: ChildAccessToken) => {
    const buttonKey = `${token.childId}-${token.dashboardType}`;

    try {
      // Modern clipboard API with fallback
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(token.accessUrl);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = token.accessUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      // Show floating snackbar message
      setSnackbarMessage(`${token.childName}'s access URL copied to clipboard!`);
      setShowFloatingSnackbar(true);

      // Hide floating snackbar after 10 seconds
      setTimeout(() => {
        setSnackbarMessage(null);
        setShowFloatingSnackbar(false);
      }, 10000);

      // Update button state to show check icon
      setCopyButtonStates(prev => ({ ...prev, [buttonKey]: true }));

      // Revert button state after 2 seconds
      setTimeout(() => {
        setCopyButtonStates(prev => ({ ...prev, [buttonKey]: false }));
      }, 2000);

    } catch {
      setError(`Failed to copy ${token.childName}'s URL to clipboard. Please try again.`);
    }
  };

  // Helper function to render subscription status (Issue #1028)
  const renderSubscriptionStatus = () => {
    // Loading state
    if (subscriptionLoading) {
      return (
        <Box data-testid="subscription-status-loading" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={20} sx={{ color: 'white' }} />
        </Box>
      );
    }

    // Error state
    if (subscriptionError && subscriptionError !== 'not_found') {
      return (
        <Typography variant="h6" sx={naluHeroValueSx}>
          Unable to load subscription status
        </Typography>
      );
    }

    // No data state - show subscribe button for primary caregivers (Issue #1150)
    if (!subscriptionStatus || subscriptionError === 'not_found' || subscriptionStatus.status === 'none') {
      return (
        <Box>
          <Typography variant="h6" gutterBottom sx={naluHeroValueSx}>
            No subscription information available
          </Typography>
          {isPrimaryCaregiver && (
            <Button
              variant="contained"
              size="small"
              disabled={checkoutLoading}
              sx={{
                bgcolor: alpha(theme.palette.common.white, 0.2),
                '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.3) },
                whiteSpace: 'nowrap',
                mt: 1
              }}
              onClick={openCheckout}
            >
              {checkoutLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Subscribe Now'}
            </Button>
          )}
        </Box>
      );
    }

    // Free account state
    if (subscriptionStatus.isFreeAccount) {
      return (
        <Box>
          <Typography variant="h6" sx={naluHeroValueSx}>
            Free Account
          </Typography>
          {subscriptionStatus.freeAccountExpiresAt && (
            <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
              Expires: {new Date(subscriptionStatus.freeAccountExpiresAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </Typography>
          )}
        </Box>
      );
    }

    // Trial state
    if (subscriptionStatus.status === 'trial' && subscriptionStatus.trialEndsAt) {
      const now = new Date();
      const endDate = new Date(subscriptionStatus.trialEndsAt);
      const hoursRemaining = (endDate.getTime() - now.getTime()) / (1000 * 60 * 60);
      const daysRemaining = Math.ceil(hoursRemaining / 24);

      let trialText = '';
      // Less than 24 hours remaining
      if (hoursRemaining < 24) {
        trialText = 'Trial expires today';
      }
      // Exactly 1 day (24-48 hours)
      else if (daysRemaining === 1 || (hoursRemaining >= 24 && hoursRemaining < 48)) {
        trialText = '1 day left in trial';
      }
      // Multiple days
      else {
        trialText = `${daysRemaining} days left in trial`;
      }

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="h6" sx={naluHeroValueSx}>
            {trialText}
          </Typography>
          {isPrimaryCaregiver && (
            <Button
              variant="contained"
              size="small"
              disabled={checkoutLoading}
              sx={{
                bgcolor: alpha(theme.palette.common.white, 0.2),
                '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.3) },
                whiteSpace: 'nowrap'
              }}
              onClick={openCheckout}
            >
              {checkoutLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Subscribe Now'}
            </Button>
          )}
        </Box>
      );
    }

    // Expired trial state
    if (subscriptionStatus.status === 'expired') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="h6" sx={naluHeroValueSx}>
            Trial Expired
          </Typography>
          {isPrimaryCaregiver && (
            <Button
              variant="contained"
              size="small"
              disabled={checkoutLoading}
              sx={{
                bgcolor: alpha(theme.palette.common.white, 0.2),
                '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.3) },
                whiteSpace: 'nowrap'
              }}
              onClick={openCheckout}
            >
              {checkoutLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Subscribe Now'}
            </Button>
          )}
        </Box>
      );
    }

    // Active subscription state
    if (subscriptionStatus.status === 'active' && !subscriptionStatus.isFreeAccount) {
      const planName = subscriptionStatus.planName || 'Annual Family Plan';
      const expirationDate = subscriptionStatus.currentPeriodEnd
        ? new Date(subscriptionStatus.currentPeriodEnd).toLocaleDateString('en-US')
        : 'N/A';

      const isBillingOwner = subscriptionStatus.billingOwnerUserId === user?.userId;

      // Lookup billing owner name from family members
      const billingOwner = familyMembers.find(m => m.userId === subscriptionStatus.billingOwnerUserId);
      const billingOwnerName = billingOwner
        ? `${billingOwner.firstName} ${billingOwner.lastName}`
        : 'the billing owner';

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="h6" sx={naluHeroValueSx}>
            {planName}: Expires {expirationDate}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {isBillingOwner && (
              <>
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    textTransform: 'none',
                    // fontSize matches the button variant's own baseline; not a Typography override
                    fontSize: '0.875rem',
                    whiteSpace: 'nowrap',
                    bgcolor: alpha(theme.palette.common.white, 0.2),
                    '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.3) }
                  }}
                  onClick={handleManageBilling}
                >
                  Manage Billing
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="error"
                  sx={{
                    textTransform: 'none',
                    // fontSize matches the button variant's own baseline; not a Typography override
                    fontSize: '0.875rem',
                    whiteSpace: 'nowrap'
                  }}
                  onClick={handleOpenCancelDialog}
                >
                  Turn Off Auto-Renew
                </Button>
              </>
            )}
            {!isBillingOwner && (
              <Typography variant="body2" sx={{ opacity: 0.7, fontStyle: 'italic' }}>
                Contact {billingOwnerName} to manage billing
              </Typography>
            )}
          </Box>
        </Box>
      );
    }

    // Cancelled subscription (grace period) state
    if (subscriptionStatus.status === 'cancelled') {
      const accessUntilDate = subscriptionStatus.currentPeriodEnd
        ? new Date(subscriptionStatus.currentPeriodEnd).toLocaleDateString('en-US')
        : 'N/A';

      const isBillingOwner = subscriptionStatus.billingOwnerUserId === user?.userId;

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label="Auto-Renew Off" size="small" color="warning" />
            <Typography variant="h6" sx={naluHeroValueSx}>
              Access until {accessUntilDate}
            </Typography>
          </Box>
          {isBillingOwner && (
            <Button
              variant="contained"
              size="small"
              disabled={checkoutLoading}
              sx={{
                textTransform: 'none',
                // fontSize matches the button variant's own baseline; not a Typography override
                fontSize: '0.875rem',
                whiteSpace: 'nowrap',
                bgcolor: alpha(theme.palette.common.white, 0.2),
                '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.3) }
              }}
              onClick={openCheckout}
            >
              {checkoutLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Resubscribe'}
            </Button>
          )}
        </Box>
      );
    }

    // Fallback
    return (
      <Typography variant="h6" sx={naluHeroValueSx}>
        No subscription information available
      </Typography>
    );
  };

  const handleManageBilling = async () => {
    try {
      const { url } = await subscriptionAPI.getCustomerPortalUrl();
      window.open(url, '_blank');
    } catch (err: unknown) {
      console.error('Error opening customer portal:', err);
      const errorMessage = getErrorMessage(err, 'error') || 'Failed to open customer portal';
      setError(errorMessage);
    }
  };

  const handleOpenCancelDialog = () => {
    setCancelSubscriptionDialogOpen(true);
  };

  const handleConfirmCancelSubscription = async () => {
    try {
      setCancellingSubscription(true);
      setError(null);

      const updatedStatus = await subscriptionAPI.cancelSubscription();

      // Update local subscription status
      setSubscriptionStatus(updatedStatus);

      // Close dialog
      setCancelSubscriptionDialogOpen(false);

      // Show success message
      setSuccessMessage('Auto-renew turned off. You will retain full access until your current period ends.');
      setShowSnackbar(true);
      setTimeout(() => {
        setSuccessMessage(null);
        setShowSnackbar(false);
      }, 7000);

      // Refresh family data to update subscription section
      loadFamilyData();
    } catch (err: unknown) {
      console.error('Error cancelling subscription:', err);
      const errorMessage = getErrorMessage(err, 'error') || 'Failed to cancel subscription';
      setError(errorMessage);
      setCancelSubscriptionDialogOpen(false);
    } finally {
      setCancellingSubscription(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <SharedAppBar title={`My ${Term}`} />
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={60} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <SharedAppBar title="My Family" />
      
      <Box sx={{ flex: 1, p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Family Information Section */}
          <Grid size={{ xs: 12 }}>
            <Paper
              data-testid="family-info-section"
              sx={{
                p: 3,
                background: isNaluV1Theme
                  ? theme.palette.primary.dark
                  : (theme.palette.gradient?.primary || `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.light} 100%)`),
                color: 'white'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SettingsIcon />
                  {Term} Information
                </Typography>
                {isPrimaryCaregiver && (
                  <Button
                    data-testid="edit-family-info-button"
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={handleEditFamilyInfo}
                    sx={isNaluV1Theme ? {
                      bgcolor: 'common.white',
                      color: 'primary.dark',
                      fontWeight: 800,
                      borderRadius: '999px',
                      '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.85) }
                    } : {
                      bgcolor: alpha(theme.palette.common.white, 0.2),
                      '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.3) }
                    }}
                  >
                    Edit
                  </Button>
                )}
              </Box>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="body2" sx={naluHeroLabelSx}>{Term} Name</Typography>
                  <Typography
                    data-testid="current-family-name"
                    variant="h6"
                    sx={naluHeroValueSx}
                  >
                    {familyInfo?.name || 'Loading...'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="body2" sx={naluHeroLabelSx}>Timezone</Typography>
                  <Typography
                    data-testid="current-family-timezone"
                    variant="h6"
                    sx={naluHeroValueSx}
                  >
                    {familyInfo?.timezone || 'UTC'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="body2" sx={naluHeroLabelSx}>Daily Logging Goal Per Child</Typography>
                  <Typography
                    data-testid="current-daily-logging-goal"
                    variant="h6"
                    sx={naluHeroValueSx}
                  >
                    {familyInfo?.effectiveDailyLoggingGoalPerChild !== null && familyInfo?.effectiveDailyLoggingGoalPerChild !== undefined
                      ? `${familyInfo.effectiveDailyLoggingGoalPerChild} ${familyInfo.effectiveDailyLoggingGoalPerChild === 1 ? 'Behavior' : 'Behaviors'}`
                      : 'Default'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="body2" sx={naluHeroLabelSx}>Subscription Status</Typography>
                  {renderSubscriptionStatus()}
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Family Members Section */}
          <Grid size={{ xs: 12 }}>
            <Paper
              data-testid="family-members-section"
              elevation={isNaluV1Theme ? 0 : undefined}
              sx={isNaluV1Theme ? { p: 0, bgcolor: 'transparent', border: 'none' } : { p: 3 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GroupIcon />
                  {Term} Members
                </Typography>
              </Box>

              <Divider sx={{ my: 3, ...(isNaluV1Theme ? { borderColor: 'transparent' } : {}) }} />

              {/* Adults Section */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography
                  variant="h6"
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, ...(isNaluV1Theme ? { fontSize: '22px', fontWeight: 600 } : {}) }}
                >
                  <AdminIcon />
                  Adults
                </Typography>
                {isPrimaryCaregiver && (
                  <Button
                    data-testid="add-adult-button"
                    variant="contained"
                    startIcon={<PersonAddIcon />}
                    onClick={handleAddMember}
                    sx={isNaluV1Theme ? { borderRadius: '999px' } : undefined}
                  >
                    Add Adult
                  </Button>
                )}
              </Box>
              <Box data-testid="adult-members-list" sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  {familyMembers.map(member => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={member.userId}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Avatar
                              src={getAvatarUrl(member.profileImageUrl)}
                              sx={isNaluV1Theme
                                ? { mr: 2, width: 48, height: 48, bgcolor: 'primary.main', color: 'primary.contrastText' }
                                : { mr: 2, width: 48, height: 48 }}
                            >
                              {member.firstName[0]}{member.lastName[0]}
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" component="div">
                                {member.firstName} {member.lastName}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {member.email}
                              </Typography>
                            </Box>
                          </Box>
                          
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                            <Chip 
                              label={member.role} 
                              size="small" 
                              color="primary"
                              onClick={isPrimaryCaregiver ? () => handleEditMemberRole(member) : undefined}
                              sx={isPrimaryCaregiver ? { cursor: 'pointer' } : {}}
                            />
                            {member.isPrimaryCaregiver && (
                              <Chip 
                                label="Primary Caregiver" 
                                size="small" 
                                color="secondary"
                              />
                            )}
                          </Box>
                        </CardContent>
                        
                        {isPrimaryCaregiver && (
                          <CardActions>
                            <Tooltip title="Edit role">
                              <IconButton
                                data-testid="edit-role-button"
                                onClick={() => handleEditMemberRole(member)}
                                color="primary"
                                size="small"
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            {member.userId !== user?.userId && (() => {
                              const restrictionReason = getRemovalRestrictionReason(member.userId, familyMembers);
                              const isDisabled = restrictionReason !== null;
                              const tooltipTitle = restrictionReason || "Remove from family";

                              return (
                                <Tooltip title={tooltipTitle}>
                                  <span> {/* Wrapper span needed for tooltip on disabled button */}
                                    <IconButton
                                      data-testid="remove-member-button"
                                      onClick={() => !isDisabled && handleRemoveMember(member)}
                                      color="error"
                                      size="small"
                                      disabled={isDisabled}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              );
                            })()}
                          </CardActions>
                        )}
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* Pending Invitations Section */}
              {pendingInvitations.length > 0 && (
                <>
                  <Divider sx={{ my: 3, ...(isNaluV1Theme ? { borderColor: 'transparent' } : {}) }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, ...(isNaluV1Theme ? { fontSize: '22px', fontWeight: 600 } : {}) }}
                    >
                      <PersonAddIcon />
                      Pending Invitations ({pendingInvitations.length})
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    {pendingInvitations.map((invitation) => (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={invitation.id}>
                        <Card 
                          variant="outlined"
                          sx={{ borderColor: 'warning.main', borderStyle: 'dashed' }}
                          data-testid={`invitation-card-${invitation.id}`}
                        >
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Avatar sx={{ mr: 2, width: 48, height: 48, bgcolor: 'warning.main', color: 'warning.contrastText' }}>
                                {invitation.firstName && invitation.lastName 
                                  ? invitation.firstName[0] + invitation.lastName[0]
                                  : invitation.email[0].toUpperCase()}
                              </Avatar>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" component="div">
                                  {invitation.firstName && invitation.lastName 
                                    ? `${invitation.firstName} ${invitation.lastName}`
                                    : invitation.email}
                                </Typography>
                                {invitation.firstName && invitation.lastName && (
                                  <Typography variant="body2" color="text.secondary">
                                    {invitation.email}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                              <Chip 
                                label={invitation.customRole || (invitation.role === 1 ? 'Owner' : 'Adult')} 
                                size="small" 
                                color="warning"
                              />
                              <Chip 
                                label="Pending" 
                                size="small" 
                                color="default"
                                variant="outlined"
                              />
                            </Box>

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Invited by: {invitation.invitedByName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Sent: {new Date(invitation.invitedAt).toLocaleDateString()}
                            </Typography>
                            {invitation.message && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                                &quot;{invitation.message}&quot;
                              </Typography>
                            )}
                          </CardContent>
                          <CardActions>
                            <Button
                              size="small"
                              onClick={() => handleResendInvitation(invitation.id)}
                              data-testid={`resend-invitation-${invitation.id}`}
                            >
                              Resend
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => handleCancelInvitation(invitation)}
                              data-testid={`cancel-invitation-${invitation.id}`}
                            >
                              Cancel Invitation
                            </Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}

              <Divider sx={{ my: 3, ...(isNaluV1Theme ? { borderColor: 'transparent' } : {}) }} />

              {/* Children Section */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography
                  variant="h6"
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, ...(isNaluV1Theme ? { fontSize: '22px', fontWeight: 600 } : {}) }}
                >
                  <ChildIcon />
                  Children
                </Typography>
                {isPrimaryCaregiver && (
                  <Button
                    data-testid="add-child-button"
                    variant="contained"
                    color={isNaluV1Theme ? 'secondary' : undefined}
                    startIcon={<PersonAddIcon />}
                    onClick={handleAddChild}
                    sx={isNaluV1Theme ? { borderRadius: '999px' } : undefined}
                  >
                    Add Child
                  </Button>
                )}
              </Box>
              <Box data-testid="children-list">
                <Grid container spacing={2}>
                  {children.map((child, index) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={child.childId}>
                      <Card
                        variant="outlined"
                        sx={isNaluV1Theme
                          ? { height: '100%', bgcolor: getChildColor(index), border: `1px solid ${getChildBorderColor(index)}` }
                          : { height: '100%' }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Avatar
                              src={getAvatarUrl(child.profileImageUrl)}
                              sx={isNaluV1Theme
                                ? { mr: 2, width: 48, height: 48, bgcolor: getChildBorderColor(index), color: 'common.white' }
                                : { mr: 2, width: 48, height: 48 }}
                            >
                              {child.name[0]}
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" component="div">
                                {child.name}
                              </Typography>
                              {child.aliases && child.aliases.length > 0 && (
                                <Typography variant="body2" color="text.secondary">
                                  Also known as: {child.aliases.join(', ')}
                                </Typography>
                              )}
                              <Typography variant="body2" color="text.secondary">
                                Age: {child.age}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>


                        {isPrimaryCaregiver && (
                          <CardActions>
                            <Tooltip title="Edit child profile">
                              <IconButton
                                onClick={() => handleEditChild(child)}
                                size="small"
                                data-testid={`edit-child-button-${child.childId}`}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove child">
                              <IconButton
                                onClick={() => handleRemoveChild(child)}
                                color="error"
                                size="small"
                                data-testid={`remove-child-button-${child.childId}`}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </CardActions>
                        )}
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Paper>
          </Grid>

          {/* Away Schedule Section */}
          <Grid size={{ xs: 12 }}>
            <Paper data-testid="away-schedule-section" sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FlightTakeoffIcon />
                  Away Schedule
                </Typography>
                {awayScheduleLoading && <CircularProgress size={20} />}
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Mark days when your children are at the other household. Away days don&apos;t count against hearts,
                streaks, or chore requirements — but hearts earned on away days still count.
              </Typography>

              {awayScheduleError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAwayScheduleError(null)}>
                  {awayScheduleError}
                </Alert>
              )}

              {/* Recurring weekly days */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Recurring away days</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((label, dayIndex) => {
                  const isSelected = awaySchedule.recurringDaysOfWeek.includes(dayIndex);
                  return (
                    <Chip
                      key={dayIndex}
                      label={label}
                      onClick={() => handleToggleAwayDay(dayIndex)}
                      color={isSelected ? 'primary' : 'default'}
                      variant={isSelected ? 'filled' : 'outlined'}
                      disabled={awayScheduleLoading}
                      aria-pressed={isSelected}
                      aria-label={`${label} — ${isSelected ? 'away' : 'present'}`}
                      // bgcolor/color are intentionally left to the Chip's own `color` prop
                      // above — it already resolves to primary.main + primary.contrastText,
                      // which nalu-v1 deliberately sets to black for WCAG contrast (issue #1900).
                      sx={isNaluV1Theme ? { borderRadius: '999px', fontWeight: 800 } : undefined}
                    />
                  );
                })}
              </Box>

              {/* Per-date overrides via calendar */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Specific date overrides</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Click a day to toggle. Overrides apply on top of recurring days.
              </Typography>
              <AwayScheduleCalendar
                schedule={awaySchedule}
                loading={awayScheduleLoading}
                onToggleDate={handleCalendarToggleDate}
                pastDayStatus={pastDayStatus}
                onMonthChange={handleAwayCalendarMonthChange}
                familyTimezone={familyInfo?.timezone}
              />
            </Paper>
          </Grid>

          {/* Child Access Management Section */}
          <Grid size={{ xs: 12 }}>
            <Paper data-testid="child-access-section" sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SecurityIcon />
                  Child Access Management
                </Typography>
              </Box>

              {/* Helper text for child access URLs */}
              <Typography
                data-testid="child-access-helper-text"
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3, lineHeight: 1.6 }}
              >
                {isDesktop ? (
                  "These URLs are intended to be used on a child's device, allowing them to see their dashboard without a parent having to log in. On an iPad/iPhone, load the page with the below URL, then tap the Share icon (the box with an arrow pointing up) and select Add to Home Screen. On an Android device, load the page and tap the three-dot menu and select Add to Home Screen. On desktop computers, bookmark the page in your browser for easy access. If you regenerate the URL, that child's link will no longer be valid. This could be used if a device were lost."
                ) : (
                  "These URLs let children see their dashboard without parent login. iPad/iPhone: tap Share icon > Add to Home Screen. Android: tap three-dot menu > Add to Home Screen. Desktop: bookmark the page. Regenerating a URL invalidates the old link."
                )}
              </Typography>

              <div data-testid="child-access-urls">
                {childAccessTokens.length === 0 ? (
                  <Typography color="text.secondary">
                    No child access URLs available. Add children to generate access links.
                  </Typography>
                ) : (
                  <Grid container spacing={2}>
                    {childAccessTokens.map(token => (
                      <Grid size={{ xs: 12, md: 6 }} key={`${token.childId}-${token.dashboardType}`}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="h6">{token.childName}</Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {token.dashboardType} Dashboard
                            </Typography>
                            <TextField
                              fullWidth
                              value={token.accessUrl.replace('/family/', `${basePath}/`)}
                              InputProps={{
                                readOnly: true,
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <Tooltip title="Copy URL to clipboard">
                                      <IconButton
                                        aria-label="Copy URL to clipboard"
                                        onClick={() => handleCopyUrl(token)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleCopyUrl(token);
                                          }
                                        }}
                                        edge="end"
                                        data-testid={`copy-url-button-${token.childId}`}
                                        tabIndex={0}
                                      >
                                        {copyButtonStates[`${token.childId}-${token.dashboardType}`] ? (
                                          <CheckIcon data-testid={`check-icon-${token.childId}`} />
                                        ) : (
                                          <ContentCopyIcon data-testid={`copy-icon-${token.childId}`} />
                                        )}
                                      </IconButton>
                                    </Tooltip>
                                  </InputAdornment>
                                )
                              }}
                              size="small"
                              sx={isNaluV1Theme
                                ? { mt: 1, '& .MuiOutlinedInput-input': { fontFamily: 'monospace', fontSize: '0.75rem' } }
                                : { mt: 1 }}
                            />
                            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                              Created: {new Date(token.createdAt).toLocaleDateString()}
                              {token.lastUsed && ` • Last used: ${new Date(token.lastUsed).toLocaleDateString()}`}
                            </Typography>
                          </CardContent>
                          {isPrimaryCaregiver && (
                            <CardActions>
                              <Button
                                data-testid="regenerate-token-button"
                                size="small"
                                startIcon={<RefreshIcon />}
                                onClick={() => handleRegenerateToken(token)}
                              >
                                Regenerate URL
                              </Button>
                            </CardActions>
                          )}
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </div>
            </Paper>
          </Grid>

          {/* Calendar Sources Section (admin-only while calendar integration is in testing) */}
          {familyInfo && user?.isAdmin && isPrimaryCaregiver && (
            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 3 }}>
                <CalendarSourcesSection familyId={familyInfo.familyId} onConnectionsChanged={handleCalendarConnectionsChanged} />
              </Paper>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Edit Family Info Modal */}
      <EditFamilyInfoModal
        open={editFamilyInfoModalOpen}
        familyInfo={familyInfo}
        onClose={() => setEditFamilyInfoModalOpen(false)}
        onSave={handleSaveFamilyInfo}
      />

      {/* Add Family Member Modal */}
      <AddMemberModal
        open={addMemberModalOpen}
        formData={addMemberForm}
        firstNameError={firstNameError}
        lastNameError={lastNameError}
        memberEmailError={memberEmailError}
        passwordError={passwordError}
        confirmPasswordError={confirmPasswordError}
        showPassword={showPassword}
        showConfirmPassword={showConfirmPassword}
        addingMember={addingMember}
        roleSuggestions={roleSuggestions}
        loadingRoles={loadingRoles}
        onClose={() => setAddMemberModalOpen(false)}
        onSave={handleSaveNewMember}
        onFormChange={(updates) => setAddMemberForm({ ...addMemberForm, ...updates })}
        onFirstNameChange={handleFirstNameChange}
        onLastNameChange={handleLastNameChange}
        onMemberEmailChange={handleMemberEmailChange}
        onPasswordChange={handlePasswordChange}
        onConfirmPasswordChange={handleConfirmPasswordChange}
        onToggleShowPassword={() => setShowPassword(!showPassword)}
        onToggleShowConfirmPassword={() => setShowConfirmPassword(!showConfirmPassword)}
        onLoadRoleSuggestions={loadRoleSuggestions}
      />



      {/* Remove Member Confirmation Dialog */}
      <RemoveMemberDialog
        open={removeMemberDialogOpen}
        member={selectedMember}
        familyMembers={familyMembers}
        removing={false}
        onClose={() => setRemoveMemberDialogOpen(false)}
        onConfirm={confirmRemoveMember}
      />

      {/* Regenerate Token Confirmation Dialog */}
      <RegenerateTokenDialog
        open={regenerateTokenDialogOpen}
        token={selectedToken}
        regenerating={regeneratingToken}
        onClose={() => setRegenerateTokenDialogOpen(false)}
        onConfirm={confirmRegenerateToken}
      />

      {/* Remove Child Confirmation Dialog */}
      <RemoveChildDialog
        open={removeChildDialogOpen}
        child={selectedChildForRemoval}
        removing={false}
        onClose={() => setRemoveChildDialogOpen(false)}
        onConfirm={confirmRemoveChild}
      />

      {/* Cancel Invitation Confirmation Dialog */}
      <CancelInvitationDialog
        open={cancelInvitationDialogOpen}
        invitation={selectedInvitation}
        canceling={false}
        onClose={handleCancelInvitationDialog}
        onConfirm={handleConfirmCancelInvitation}
      />

      {/* Cancel Subscription Confirmation Dialog */}
      <Dialog
        open={cancelSubscriptionDialogOpen}
        onClose={() => !cancellingSubscription && setCancelSubscriptionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Turn Off Auto-Renew?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to turn off auto-renew?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            You will retain full access until {subscriptionStatus?.currentPeriodEnd
              ? new Date(subscriptionStatus.currentPeriodEnd).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })
              : 'your current period ends'}. After that, your account will be locked. Your data will be preserved for 1 year, allowing you to resubscribe and regain access anytime. You can also delete your data at any time from Account Settings.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCancelSubscriptionDialogOpen(false)}
            disabled={cancellingSubscription}
          >
            Keep Auto-Renew
          </Button>
          <Button
            onClick={handleConfirmCancelSubscription}
            color="warning"
            variant="contained"
            disabled={cancellingSubscription}
          >
            {cancellingSubscription ? <CircularProgress size={20} /> : 'Turn Off Auto-Renew'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Avatar Upload Modal */}
      <AvatarUploadModal
        isOpen={avatarModalOpen}
        onSave={handleAvatarSave}
        onClose={() => setAvatarModalOpen(false)}
        currentAvatarUrl={avatarEditingEntity?.currentAvatarUrl}
        entityType={avatarEditingEntity?.type}
        entityId={avatarEditingEntity?.id}
      />

      {/* Role Edit Modal for Existing Members */}
      <RoleEditModal
        open={roleEditModalOpen}
        onClose={() => {
          setRoleEditModalOpen(false);
          setEditingMemberRole(null);
        }}
        onSave={handleSaveMemberRole}
        currentRole={editingMemberRole?.currentRole || ''}
        userId={editingMemberRole?.userId || 0}
        familyId={familyInfo?.familyId || 0}
        isAdmin={user?.isAdmin || false}
        isPrimaryCaregiver={isPrimaryCaregiver}
      />

      {/* Floating Success Message Snackbar */}
      {showFloatingSnackbar && snackbarMessage && (
        <Alert
          severity="success"
          sx={{
            position: 'fixed',
            top: 20,
            right: 20,
            zIndex: 1400, // Higher than AppBar (typically ~1200-1300)
            boxShadow: 2
          }}
          onClose={() => {
            setSnackbarMessage(null);
            setShowFloatingSnackbar(false);
          }}
        >
          {snackbarMessage}
        </Alert>
      )}

      {/* Original Success Message Snackbar (for avatar operations) */}
      {showSnackbar && successMessage && (
        <Alert
          severity="success"
          sx={{
            position: 'fixed',
            top: 70,
            right: 20,
            zIndex: 1400, // Higher than AppBar (typically ~1200-1300)
            boxShadow: 2
          }}
        >
          {successMessage}
        </Alert>
      )}

      {/* Issue #1030 - Checkout Success Snackbar */}
      {showCheckoutSuccess && checkoutSuccessMessage && (
        <Alert
          severity="success"
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            zIndex: 1400,
            boxShadow: 2
          }}
          onClose={() => {
            setCheckoutSuccessMessage(null);
            setShowCheckoutSuccess(false);
          }}
        >
          {checkoutSuccessMessage}
        </Alert>
      )}
    </Box>
  );
};

export default MyFamilyPage;